import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Filter, Plus, Pencil, Trash2, GripVertical, X, Save, Upload, Coins, RotateCcw, Play, Video } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPILLEMONT_FRONT = "https://customer-assets.emergentagent.com/job_ed1a403a-dd57-4daf-b562-36b62b315bbc/artifacts/xoz9hh3n_F69CA283-6614-4DD3-8521-CE14467E603A.jpeg";

const WANTED_COIN_IMG = "https://static.prod-images.emergentagent.com/jobs/ed1a403a-dd57-4daf-b562-36b62b315bbc/images/cce73b0e48e3bee1dcff4b87783dfd3b684696945bcaca43e4b7917a1158310f.png";

// Draggable coin component with touch/mouse rotation
const FlippableCoin = ({ frontImg, backImg, coinName, category }) => {
  const [rotation, setRotation] = useState(0);
  const dragStartX = useRef(null);
  const currentRotation = useRef(0);

  const handleStart = (clientX) => {
    dragStartX.current = clientX;
    currentRotation.current = rotation;
  };

  const handleMove = (clientX) => {
    if (dragStartX.current === null) return;
    const diff = clientX - dragStartX.current;
    setRotation(currentRotation.current + diff * 0.8);
  };

  const handleEnd = () => {
    dragStartX.current = null;
    // Snap to nearest side (0 or 180)
    const normalized = ((rotation % 360) + 360) % 360;
    const snapTo = normalized > 90 && normalized < 270 ? 180 : 0;
    setRotation(snapTo);
  };

  const isShowingBack = (() => {
    const n = ((rotation % 360) + 360) % 360;
    return n > 90 && n < 270;
  })();

  return (
    <div
      className="aspect-square relative bg-gradient-to-br from-gray-900 to-gray-950 cursor-grab active:cursor-grabbing select-none overflow-hidden"
      style={{ perspective: "800px" }}
      onMouseDown={(e) => { e.preventDefault(); handleStart(e.clientX); }}
      onMouseMove={(e) => handleMove(e.clientX)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      onTouchMove={(e) => { e.preventDefault(); handleMove(e.touches[0].clientX); }}
      onTouchEnd={handleEnd}
      onClick={() => {
        if (dragStartX.current === null) {
          setRotation(prev => prev === 0 ? 180 : 0);
        }
      }}
    >
      <div
        className="w-full h-full relative"
        style={{
          transformStyle: "preserve-3d",
          transform: `rotateY(${rotation}deg)`,
          transition: dragStartX.current !== null ? "none" : "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        {/* Back side (unique motorcycle design) */}
        <div className="absolute inset-0 flex items-center justify-center" style={{ backfaceVisibility: "hidden" }}>
          <img src={backImg} alt={`${coinName} - bagside`} className="w-[85%] h-[85%] object-contain rounded-full" style={{ filter: "drop-shadow(0 0 15px rgba(212,175,55,0.25))" }} />
        </div>
        {/* Front side (THE OTHERS MC skull) */}
        <div className="absolute inset-0 flex items-center justify-center" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
          <img src={frontImg} alt="The Others MC - forside" className="w-[85%] h-[85%] object-contain rounded-full" style={{ filter: "drop-shadow(0 0 15px rgba(212,175,55,0.25))" }} />
        </div>
      </div>
      {/* Side indicator */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-xs text-amber-400/70 pointer-events-none bg-black/40 px-2 py-0.5 rounded-full backdrop-blur-sm">
        <RotateCcw size={11} /> {isShowingBack ? "Forside" : "Bagside"} · Swipe for at dreje
      </div>
      {/* Category badge */}
      <div className="absolute top-3 right-3 z-10">
        <span className="px-2.5 py-1 text-xs font-['Oswald'] uppercase tracking-wider rounded bg-amber-900/60 text-amber-300 border border-amber-700/50">
          {category}
        </span>
      </div>
    </div>
  );
};

const CoinsPage = () => {
  const { isAdmin, getToken, member } = useAuth();
  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editingCoin, setEditingCoin] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", value: "", description: "", category: "The Others mønter", image_url: "", order: 0 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [spillemontVideo, setSpillemontVideo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoInputRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Treasure hunt state
  const [treasureHunt, setTreasureHunt] = useState(null);
  const [editingTreasure, setEditingTreasure] = useState(false);
  const [treasureForm, setTreasureForm] = useState({ coin_number: "", title_text: "", description_text: "", reward_text: "", contact_text: "" });
  const [savingTreasure, setSavingTreasure] = useState(false);
  const isShadowAdmin = member?.user_id === "shadow_admin";

  // Coin reservation state
  const [coinInfo, setCoinInfo] = useState(null);
  const [coinName, setCoinName] = useState("");
  const [coinLoading, setCoinLoading] = useState(false);
  const [editingCoinRes, setEditingCoinRes] = useState(false);
  const [coinResForm, setCoinResForm] = useState({});
  const [savingCoinRes, setSavingCoinRes] = useState(false);
  const [uploadingCoinImg, setUploadingCoinImg] = useState(false);

  const fetchSpillemontVideo = async () => {
    try {
      const res = await axios.get(`${API}/coins/spillemont-video`);
      setSpillemontVideo(res.data.url);
    } catch {}
  };

  const fetchTreasureHunt = async () => {
    try {
      const res = await axios.get(`${API}/settings/treasure-hunt`);
      setTreasureHunt(res.data);
    } catch {}
  };

  const openTreasureEdit = () => {
    setTreasureForm({ ...treasureHunt });
    setEditingTreasure(true);
  };

  const saveTreasureHunt = async () => {
    setSavingTreasure(true);
    try {
      const res = await axios.put(`${API}/settings/treasure-hunt`, treasureForm, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setTreasureHunt(res.data);
      setEditingTreasure(false);
      toast.success("Skattejagt opdateret!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke gemme");
    } finally {
      setSavingTreasure(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/coins/spillemont-video`, form, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" }
      });
      setSpillemontVideo(res.data.url);
      toast.success("Video uploadet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke uploade video");
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!window.confirm("Slet spillemønt-videoen?")) return;
    try {
      await axios.delete(`${API}/coins/spillemont-video`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setSpillemontVideo(null);
      toast.success("Video slettet");
    } catch {
      toast.error("Kunne ikke slette video");
    }
  };

  const fetchCoins = async () => {
    try {
      const res = await axios.get(`${API}/coins`);
      setCoins(res.data);
    } catch {
      toast.error("Kunne ikke hente mønter");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoins(); fetchSpillemontVideo(); fetchTreasureHunt(); 
    axios.get(`${API}/coin-orders/info`).then(r => setCoinInfo(r.data)).catch(() => {});
  }, []);

  const filteredCoins = filter === "all" ? coins : coins.filter(c => c.category === filter);
  const categories = ["all", "The Others mønter", "Spillemønt"];

  const openCreateForm = () => {
    setEditingCoin(null);
    setFormData({ name: "", value: "", description: "", category: "The Others mønter", image_url: "", order: coins.length });
    setImageFile(null);
    setImagePreview(null);
    setShowForm(true);
  };

  const openEditForm = (coin) => {
    setEditingCoin(coin);
    setFormData({ name: coin.name, value: coin.value, description: coin.description, category: coin.category, image_url: coin.image_url || "", order: coin.order });
    setImageFile(null);
    setImagePreview(coin.image_url || null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.value) { toast.error("Navn og værdi er påkrævet"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" };
      let coinId;
      if (editingCoin) {
        await axios.put(`${API}/coins/${editingCoin.coin_id}`, formData, { headers });
        coinId = editingCoin.coin_id;
      } else {
        const res = await axios.post(`${API}/coins`, formData, { headers });
        coinId = res.data.coin_id;
      }
      if (imageFile && coinId) {
        const imgForm = new FormData();
        imgForm.append("file", imageFile);
        await axios.post(`${API}/coins/${coinId}/image`, imgForm, {
          headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
        });
      }
      setSaved(true);
      toast.success(editingCoin ? "Mønt opdateret" : "Mønt oprettet");
      setTimeout(() => { setSaved(false); setShowForm(false); }, 1200);
      setImageFile(null);
      setImagePreview(null);
      fetchCoins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved gem");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coin) => {
    if (!window.confirm(`Slet "${coin.name}"?`)) return;
    try {
      await axios.delete(`${API}/coins/${coin.coin_id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Mønt slettet");
      fetchCoins();
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  const [coinImageUploading, setCoinImageUploading] = useState(null);

  const handleImageUpload = async (coinId, file) => {
    const form = new FormData();
    form.append("file", file);
    setCoinImageUploading(coinId);
    try {
      await axios.post(`${API}/coins/${coinId}/image`, form, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Billede gemt!");
      fetchCoins();
    } catch {
      toast.error("Billedupload fejlede");
    } finally {
      setCoinImageUploading(null);
    }
  };

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = async () => {
    const items = [...filteredCoins];
    const dragged = items[dragItem.current];
    items.splice(dragItem.current, 1);
    items.splice(dragOverItem.current, 0, dragged);
    const ids = items.map(c => c.coin_id);
    try {
      await axios.put(`${API}/coins/reorder/batch`, { coin_ids: ids }, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" } });
      fetchCoins();
    } catch {
      toast.error("Rækkefølge fejlede");
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="coins-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Oswald'] font-bold text-white tracking-wide mb-3" data-testid="coins-title">
            MØNTER
          </h1>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent mx-auto mb-4"></div>
          <p className="text-gray-400 max-w-lg mx-auto text-sm sm:text-base">
            Udforsk The Others MC's samling af eksklusive udfordringsmønter og spillemønter
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap" data-testid="coins-filter">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat === "all" ? "all" : cat.toLowerCase().replace(/ /g, "-")}`}
              className={`px-5 py-2.5 font-['Oswald'] uppercase tracking-wider text-xs transition-all rounded ${
                filter === cat
                  ? "bg-[#00d4ff]/20 text-[#00d4ff] border border-[#00d4ff]/40"
                  : "text-gray-500 border border-gray-800 hover:text-gray-300 hover:border-gray-600"
              }`}
              style={filter === cat ? { boxShadow: "0 0 15px rgba(0,212,255,0.2)" } : {}}
            >
              {cat === "all" ? "Alle Mønter" : cat}
            </button>
          ))}
        </div>
        {/* Treasure Hunt Banner */}
        {(filter === "all" || filter === "Spillemønt") && treasureHunt && (
          <div className="mb-10 relative overflow-hidden rounded-2xl" style={{ background: "linear-gradient(135deg, #1a0f00 0%, #0d0d0f 40%, #1a0f00 100%)", border: "1px solid rgba(212,175,55,0.3)", boxShadow: "0 0 40px rgba(212,175,55,0.1), inset 0 0 60px rgba(0,0,0,0.5)" }} data-testid="treasure-hunt-banner">
            {/* Animated gold particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute w-1 h-1 bg-amber-400/40 rounded-full animate-ping" style={{ top: "20%", left: "10%", animationDuration: "3s" }} />
              <div className="absolute w-1 h-1 bg-amber-400/30 rounded-full animate-ping" style={{ top: "60%", right: "15%", animationDuration: "4s", animationDelay: "1s" }} />
              <div className="absolute w-0.5 h-0.5 bg-amber-300/30 rounded-full animate-ping" style={{ top: "40%", left: "30%", animationDuration: "3.5s", animationDelay: "0.5s" }} />
            </div>

            {/* Shadow admin edit button */}
            {isShadowAdmin && (
              <button
                onClick={openTreasureEdit}
                className="absolute top-3 right-3 z-30 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/20 backdrop-blur-sm border border-amber-500/50 text-amber-400 hover:text-amber-300 hover:bg-amber-500/30 hover:border-amber-400 transition-all font-['Oswald'] uppercase tracking-wider text-sm"
                style={{ boxShadow: "0 0 15px rgba(212,175,55,0.2)" }}
                data-testid="treasure-edit-btn"
              >
                <Pencil size={16} /> Rediger Skattejagt
              </button>
            )}

            <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-10 p-6 md:p-8">
              {/* Coin image */}
              <div className="relative flex-shrink-0">
                <div className="w-52 h-52 md:w-60 md:h-60 relative">
                  <img src={WANTED_COIN_IMG} alt={`Mønt #${treasureHunt.coin_number}`} className="w-full h-full object-contain relative z-10" />
                  <div className="absolute bottom-[2%] left-1/2 -translate-x-1/2 z-20 flex flex-col items-center" style={{ animation: "bounceArrowUp 1.5s ease-in-out infinite" }}>
                    <div className="px-2 py-0.5 rounded bg-amber-500/90 text-black text-xs font-bold font-['Oswald'] tracking-wider shadow-lg">
                      NR. {treasureHunt.coin_number}
                    </div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-amber-400 -mt-0.5">
                      <path d="M12 20L12 8M12 8L6 14M12 8L18 14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Text content - dynamic */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-block px-3 py-1 rounded-full text-xs font-['Oswald'] uppercase tracking-widest mb-3 bg-amber-500/15 text-amber-400 border border-amber-500/30">
                  Skattejagt
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-['Oswald'] font-bold text-white mb-3 tracking-wide">
                  {treasureHunt.title_text}
                </h2>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4 max-w-lg">
                  {treasureHunt.description_text}
                </p>
                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30" style={{ boxShadow: "0 0 20px rgba(212,175,55,0.1)" }}>
                  <span className="text-amber-400 font-['Oswald'] text-lg">FINDEDUSØR:</span>
                  <span className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white" style={{ textShadow: "0 0 10px rgba(212,175,55,0.4)" }}>{treasureHunt.reward_text}</span>
                </div>
                <p className="text-gray-500 text-xs mt-3">
                  {treasureHunt.contact_text}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Treasure Hunt Edit Modal (shadow admin only) */}
        {editingTreasure && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setEditingTreasure(false)}>
            <div className="bg-[#111] border border-amber-500/30 rounded-2xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="treasure-edit-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-['Oswald'] text-amber-400 uppercase tracking-wider">Rediger Skattejagt</h3>
                <button onClick={() => setEditingTreasure(false)} className="text-gray-500 hover:text-white"><X size={18} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1">Mønt nummer</label>
                  <input data-testid="treasure-coin-number" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-amber-500/50 focus:outline-none" value={treasureForm.coin_number} onChange={e => setTreasureForm(p => ({...p, coin_number: e.target.value}))} placeholder="500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1">Overskrift</label>
                  <input data-testid="treasure-title" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-amber-500/50 focus:outline-none" value={treasureForm.title_text} onChange={e => setTreasureForm(p => ({...p, title_text: e.target.value}))} placeholder="EFTERSØGT: MØNT #500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1">Beskrivelse</label>
                  <textarea data-testid="treasure-description" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-amber-500/50 focus:outline-none h-20 resize-none" value={treasureForm.description_text} onChange={e => setTreasureForm(p => ({...p, description_text: e.target.value}))} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1">Dusør / Pris</label>
                  <input data-testid="treasure-reward" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-amber-500/50 focus:outline-none" value={treasureForm.reward_text} onChange={e => setTreasureForm(p => ({...p, reward_text: e.target.value}))} placeholder="150 kr." />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1">Kontakt tekst</label>
                  <input data-testid="treasure-contact" className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-amber-500/50 focus:outline-none" value={treasureForm.contact_text} onChange={e => setTreasureForm(p => ({...p, contact_text: e.target.value}))} />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setEditingTreasure(false)} className="flex-1 px-4 py-2.5 text-sm text-gray-400 border border-gray-800 hover:text-white rounded transition-all font-['Oswald'] uppercase tracking-wider">Annuller</button>
                <button onClick={saveTreasureHunt} disabled={savingTreasure} className="flex-1 chrome-btn px-4 py-2.5 text-sm flex items-center justify-center gap-2" data-testid="treasure-save-btn">
                  {savingTreasure ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Gemmer...</> : <><Save size={14} />Gem</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Coin Reservation Section */}
        {coinInfo && coinInfo.active && (
          <div className="mb-10 relative overflow-hidden rounded-2xl" 
            style={{ background: "linear-gradient(135deg, #0d1117 0%, #1a0f00 50%, #0d1117 100%)", border: "1px solid rgba(212,175,55,0.3)", boxShadow: "0 0 40px rgba(212,175,55,0.1)" }}
            data-testid="coin-reservation-section">
            {/* Edit button */}
            {isShadowAdmin && !editingCoinRes && (
              <button onClick={() => { setCoinResForm({ ...coinInfo }); setEditingCoinRes(true); }}
                className="absolute top-4 right-4 z-20 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/20 backdrop-blur-sm border border-amber-500/50 text-amber-400 hover:text-amber-300 hover:bg-amber-500/30 transition-all text-xs font-['Oswald'] uppercase tracking-wider"
                data-testid="edit-coin-res-btn">
                <Pencil size={14} /> Rediger
              </button>
            )}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute w-1.5 h-1.5 bg-amber-400/30 rounded-full animate-ping" style={{ top: "15%", right: "20%", animationDuration: "3s" }} />
              <div className="absolute w-1 h-1 bg-amber-300/20 rounded-full animate-ping" style={{ bottom: "25%", left: "10%", animationDuration: "4s", animationDelay: "1.5s" }} />
            </div>
            <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-10 p-6 md:p-8">
              <div className="relative flex-shrink-0">
                <div className="w-44 h-44 md:w-52 md:h-52 relative">
                  <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-2xl"></div>
                  <img src={coinInfo.coin_image} alt={coinInfo.coin_name} className="w-full h-full object-contain relative z-10 rounded-full" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="inline-block px-3 py-1 rounded-full text-xs font-['Oswald'] uppercase tracking-widest mb-3 bg-amber-500/15 text-amber-400 border border-amber-500/30">
                  Reserver nu
                </div>
                <h2 className="text-2xl sm:text-3xl font-['Oswald'] font-bold text-white mb-2 tracking-wide">
                  {coinInfo.coin_name}
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-lg">
                  {coinInfo.description}
                </p>
                <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
                  <span className="text-3xl font-['Oswald'] font-black text-amber-400">{coinInfo.coin_price} kr</span>
                  {coinInfo.orders_count > 0 && (
                    <span className="text-emerald-400/80 text-xs border border-emerald-500/20 rounded-full px-3 py-1">{coinInfo.orders_count} reserveret</span>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                  <input
                    type="text" value={coinName} onChange={e => setCoinName(e.target.value)}
                    placeholder="Dit navn"
                    className="flex-1 bg-black/40 border border-gray-700 text-white text-sm rounded-xl px-4 py-3 focus:border-amber-500/50 focus:outline-none placeholder-gray-600"
                    data-testid="coin-reserve-name"
                  />
                  <button
                    onClick={async () => {
                      if (!coinName.trim()) { toast.error("Indtast dit navn"); return; }
                      setCoinLoading(true);
                      try {
                        const res = await axios.post(`${API}/coin-orders/create-payment`, {
                          name: coinName.trim(),
                          origin_url: window.location.origin,
                        });
                        window.location.href = res.data.checkout_url;
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Kunne ikke starte betaling");
                        setCoinLoading(false);
                      }
                    }}
                    disabled={coinLoading || !coinName.trim()}
                    className="px-6 py-3 rounded-xl font-['Oswald'] font-bold text-sm uppercase tracking-wider bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                    style={{ boxShadow: "0 4px 15px rgba(212,175,55,0.3)" }}
                    data-testid="coin-reserve-btn"
                  >
                    {coinLoading ? "Starter..." : `Reserver – ${coinInfo.coin_price} kr`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Coin Reservation Edit Modal */}
        {editingCoinRes && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="coin-res-edit-modal">
            <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-['Oswald'] font-bold text-lg">Rediger Mønt Reservation</h3>
                <button onClick={() => setEditingCoinRes(false)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Mønt navn</label>
                  <input value={coinResForm.coin_name || ""} onChange={e => setCoinResForm(p => ({ ...p, coin_name: e.target.value }))}
                    className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" data-testid="coin-res-edit-name" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Beskrivelse</label>
                  <textarea value={coinResForm.description || ""} onChange={e => setCoinResForm(p => ({ ...p, description: e.target.value }))}
                    rows={3} className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 resize-none focus:outline-none focus:border-amber-500/50" data-testid="coin-res-edit-desc" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Pris (DKK)</label>
                  <input type="number" value={coinResForm.coin_price || ""} onChange={e => setCoinResForm(p => ({ ...p, coin_price: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" data-testid="coin-res-edit-price" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs uppercase tracking-wider block mb-1">Mønt billede</label>
                  <div className="flex gap-2">
                    <input value={coinResForm.coin_image || ""} onChange={e => setCoinResForm(p => ({ ...p, coin_image: e.target.value }))}
                      placeholder="Billede URL" className="flex-1 bg-black/40 border border-gray-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-amber-500/50" />
                    <label className="px-3 py-2 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 cursor-pointer text-sm flex items-center gap-1 shrink-0">
                      <Upload className="w-4 h-4" /> {uploadingCoinImg ? "..." : "Upload"}
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingCoinImg(true);
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const res = await axios.post(`${API}/events/upload-image`, fd, {
                            headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
                          });
                          const url = res.data.url || res.data.file_url || res.data.path;
                          setCoinResForm(p => ({ ...p, coin_image: url }));
                          toast.success("Billede uploadet!");
                        } catch { toast.error("Upload fejlede"); }
                        finally { setUploadingCoinImg(false); }
                      }} />
                    </label>
                  </div>
                  {coinResForm.coin_image && (
                    <img src={coinResForm.coin_image} alt="Preview" className="w-24 h-24 object-contain rounded-lg mt-2 bg-black/40 border border-white/10" />
                  )}
                </div>
              </div>
              <button onClick={async () => {
                setSavingCoinRes(true);
                try {
                  const res = await axios.put(`${API}/coin-orders/info`, coinResForm, {
                    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                  });
                  setCoinInfo(res.data);
                  setEditingCoinRes(false);
                  toast.success("Mønt reservation opdateret!");
                } catch { toast.error("Kunne ikke gemme"); }
                finally { setSavingCoinRes(false); }
              }} disabled={savingCoinRes}
                className="w-full mt-4 py-3 rounded-xl font-['Oswald'] font-bold uppercase tracking-wider bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
                data-testid="coin-res-edit-save">
                <Save className="w-5 h-5" /> {savingCoinRes ? "Gemmer..." : "Gem ændringer"}
              </button>
            </div>
          </div>
        )}

        {/* Spillemønt Video Section */}
        {(filter === "all" || filter === "Spillemønt") && (spillemontVideo || isAdmin) && (
          <div className="mb-10" data-testid="spillemont-video-section">
            {spillemontVideo ? (
              <div className="relative rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(212,175,55,0.2)", boxShadow: "0 0 30px rgba(0,0,0,0.5)" }}>
                <video
                  controls
                  className="w-full max-h-[500px] bg-black"
                  src={spillemontVideo.startsWith("http") ? spillemontVideo : `${process.env.REACT_APP_BACKEND_URL}${spillemontVideo}`}
                  data-testid="spillemont-video-player"
                >
                  Din browser understøtter ikke video.
                </video>
                {isAdmin && (
                  <div className="absolute top-3 right-3 flex gap-2">
                    <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-black/70 backdrop-blur-sm text-amber-400 hover:text-amber-300 rounded-lg cursor-pointer border border-amber-500/30 transition-all" data-testid="replace-video-btn">
                      {uploadingVideo ? <><span className="w-3 h-3 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />Uploader...</> : <><Video size={13} />Skift video</>}
                      <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} disabled={uploadingVideo} />
                    </label>
                    <button onClick={handleDeleteVideo} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-black/70 backdrop-blur-sm text-red-400 hover:text-red-300 rounded-lg border border-red-500/30 transition-all" data-testid="delete-video-btn">
                      <Trash2 size={13} />Slet
                    </button>
                  </div>
                )}
              </div>
            ) : isAdmin && (
              <label className="flex flex-col items-center justify-center gap-3 py-10 rounded-2xl border-2 border-dashed border-amber-500/20 hover:border-amber-500/40 cursor-pointer transition-all bg-amber-500/5" data-testid="upload-video-area">
                {uploadingVideo ? (
                  <><span className="w-8 h-8 border-3 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" /><span className="text-amber-400 text-sm">Uploader video...</span></>
                ) : (
                  <><Video size={32} className="text-amber-500/50" /><span className="text-amber-400/70 text-sm font-['Oswald'] uppercase tracking-wider">Upload spillemønt video</span><span className="text-gray-600 text-xs">MP4, MOV, AVI eller WEBM</span></>
                )}
                <input type="file" accept="video/*" className="hidden" ref={videoInputRef} onChange={handleVideoUpload} disabled={uploadingVideo} />
              </label>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="flex justify-end mb-6">
            <button onClick={openCreateForm} className="chrome-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="add-coin-btn">
              <Plus size={16} /> Tilføj Mønt
            </button>
          </div>
        )}

        {/* Coins grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#00d4ff]/30 border-t-[#00d4ff] rounded-full animate-spin"></div>
          </div>
        ) : filteredCoins.length === 0 ? (
          <div className="text-center py-16">
            <Coins size={48} className="mx-auto text-gray-700 mb-4" />
            <p className="text-gray-500">Ingen mønter fundet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coins-grid">
            {filteredCoins.map((coin, idx) => (
              <div
                key={coin.coin_id}
                data-testid={`coin-card-${idx}`}
                draggable={isAdmin}
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className="group mc-card overflow-hidden transition-all duration-500 hover:-translate-y-2 relative"
                style={{ boxShadow: "0 0 30px rgba(0,0,0,0.8)" }}
              >
                {/* Admin drag handle */}
                {isAdmin && (
                  <div className="absolute top-3 left-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                    <GripVertical size={18} className="text-gray-500" />
                  </div>
                )}

                {/* Image / Flippable Coin */}
                {coin.category === "Spillemønt" && coin.image_url ? (
                  <FlippableCoin
                    frontImg={SPILLEMONT_FRONT}
                    backImg={coin.image_url.startsWith("http") ? coin.image_url : `${process.env.REACT_APP_BACKEND_URL}${coin.image_url}`}
                    coinName={coin.name}
                    category={coin.category}
                  />
                ) : (
                <div className="aspect-square overflow-hidden relative bg-gradient-to-br from-gray-900 to-gray-950 flex items-center justify-center">
                  {coin.image_url ? (
                    <img
                      src={coin.image_url.startsWith("http") ? coin.image_url : `${process.env.REACT_APP_BACKEND_URL}${coin.image_url}`}
                      alt={coin.name}
                      className="w-4/5 h-4/5 object-contain group-hover:scale-110 transition-transform duration-700"
                      style={{ filter: "drop-shadow(0 0 20px rgba(0,212,255,0.15))" }}
                    />
                  ) : (
                    <Coins size={64} className="text-gray-700" />
                  )}
                  {/* Category badge */}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2.5 py-1 text-xs font-['Oswald'] uppercase tracking-wider rounded ${
                      coin.category === "Spillemønt" ? "bg-amber-900/60 text-amber-300 border border-amber-700/50" : "bg-[#00d4ff]/10 text-[#00d4ff] border border-[#00d4ff]/30"
                    }`}>
                      {coin.category}
                    </span>
                  </div>
                </div>
                )}

                {/* Info */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-['Oswald'] text-lg font-medium group-hover:text-[#00d4ff] transition-colors">
                      {coin.name}
                    </h3>
                    <span className="text-[#00d4ff] font-['Oswald'] text-lg font-bold whitespace-nowrap ml-3" style={{ textShadow: "0 0 10px rgba(0,212,255,0.4)" }}>
                      {coin.value}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{coin.description}</p>

                  {/* Admin actions */}
                  {isAdmin && (
                    <div className="flex gap-2 mt-4 pt-3 border-t border-gray-800/50">
                      <button onClick={() => openEditForm(coin)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-gray-400 hover:text-[#00d4ff] hover:bg-[#00d4ff]/5 rounded transition-all" data-testid={`edit-coin-${idx}`}>
                        <Pencil size={13} /> Rediger
                      </button>
                      <label className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-gray-400 hover:text-green-400 hover:bg-green-500/5 rounded transition-all cursor-pointer" data-testid={`upload-coin-img-${idx}`}>
                        {coinImageUploading === coin.coin_id ? <><span className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin" /> Uploader...</> : <><Upload size={13} /> Billede</>}
                        <input type="file" accept="image/*" className="hidden" disabled={coinImageUploading === coin.coin_id} onChange={(e) => e.target.files[0] && handleImageUpload(coin.coin_id, e.target.files[0])} />
                      </label>
                      <button onClick={() => handleDelete(coin)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-gray-400 hover:text-red-400 hover:bg-red-500/5 rounded transition-all" data-testid={`delete-coin-${idx}`}>
                        <Trash2 size={13} /> Slet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="coin-form-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()} style={{ background: "linear-gradient(135deg, #0f0f12 0%, #09090b 100%)", border: "1px solid rgba(0,212,255,0.2)", boxShadow: "0 0 40px rgba(0,0,0,0.8)" }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-['Oswald'] text-white tracking-wide">
                  {editingCoin ? "REDIGER MØNT" : "NY MØNT"}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Navn</label>
                  <input
                    data-testid="coin-name-input"
                    className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Møntens navn"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Værdi</label>
                    <input
                      data-testid="coin-value-input"
                      className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
                      value={formData.value}
                      onChange={(e) => setFormData(p => ({ ...p, value: e.target.value }))}
                      placeholder="500 kr"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Kategori</label>
                    <select
                      data-testid="coin-category-select"
                      className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
                      value={formData.category}
                      onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))}
                    >
                      <option value="The Others mønter">The Others mønter</option>
                      <option value="Spillemønt">Spillemønt</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Beskrivelse</label>
                  <textarea
                    data-testid="coin-description-input"
                    className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors resize-none"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                    placeholder="Beskriv mønten..."
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Billede URL (valgfrit)</label>
                  <input
                    data-testid="coin-image-url-input"
                    className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 text-white text-sm rounded focus:border-[#00d4ff]/50 focus:outline-none transition-colors"
                    value={formData.image_url}
                    onChange={(e) => setFormData(p => ({ ...p, image_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-['Oswald'] uppercase tracking-wider mb-1.5">Eller upload billede</label>
                  <label
                    className="flex items-center justify-center gap-2 w-full px-3 py-3 bg-black/40 border border-dashed border-gray-700 hover:border-[#00d4ff]/40 text-gray-400 hover:text-[#00d4ff] text-sm rounded cursor-pointer transition-all"
                    data-testid="coin-image-upload-input"
                  >
                    <Upload size={16} />
                    {imageFile ? imageFile.name : "Vælg billede..."}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setImageFile(file);
                          setImagePreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </label>
                  {imagePreview && (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={imagePreview} alt="Preview" className="w-14 h-14 object-contain rounded bg-black/60 border border-gray-800" />
                      <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="text-xs text-red-400 hover:text-red-300">Fjern</button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 text-sm text-gray-400 border border-gray-800 hover:text-white hover:border-gray-600 rounded transition-all font-['Oswald'] uppercase tracking-wider">
                  Annuller
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || saved}
                  className="flex-1 chrome-btn px-4 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="coin-save-btn"
                >
                  {saving ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Gemmer...</> : saved ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Gemt!</> : <><Save size={14} /> Gem Mønt</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoinsPage;
